import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PageSection, Language } from '../../../types/pageBuilder';
import SectionControls from './SectionControls';
import SectionPreview from './SectionPreview';
import SectionEditor from './SectionEditor';

interface SectionListProps {
  sections: PageSection[];
  language: Language;
  onReorder: (sections: { id: number; display_order: number }[]) => Promise<void>;
  onToggleVisibility: (sectionId: number, isVisible: boolean) => Promise<void>;
  onDelete: (sectionId: number) => Promise<void>;
  onRefresh: () => void;
}

interface SortableSectionProps {
  section: PageSection;
  language: Language;
  onEdit: () => void;
  onToggleVisibility: () => void;
  onDelete: () => void;
}

/**
 * Sortable section item
 */
const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  language,
  onEdit,
  onToggleVisibility,
  onDelete
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const isEditable = section.config?.editable !== false;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Section Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div {...attributes} {...listeners} className="cursor-move text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 8h16M4 16h16"
              />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{section.section_type}</h4>
            <p className="text-xs text-gray-500">Order: {section.display_order}</p>
          </div>
          {!isEditable && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
              Dynamic
            </span>
          )}
        </div>
        <SectionControls
          section={section}
          onEdit={onEdit}
          onToggleVisibility={onToggleVisibility}
          onDelete={onDelete}
          isEditable={isEditable}
        />
      </div>

      {/* Section Preview */}
      <div className="p-4">
        <SectionPreview section={section} />
      </div>
    </div>
  );
};

/**
 * Draggable section list component
 */
const SectionList: React.FC<SectionListProps> = ({
  sections,
  language,
  onReorder,
  onToggleVisibility,
  onDelete,
  onRefresh
}) => {
  const [localSections, setLocalSections] = useState(sections);
  const [editingSection, setEditingSection] = useState<PageSection | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Update local sections when props change
  React.useEffect(() => {
    setLocalSections(sections);
  }, [sections]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localSections.findIndex((s) => s.id === active.id);
      const newIndex = localSections.findIndex((s) => s.id === over.id);

      const reorderedSections = arrayMove(localSections, oldIndex, newIndex);
      setLocalSections(reorderedSections);

      // Prepare reorder data
      const reorderData = reorderedSections.map((section, index) => ({
        id: section.id,
        display_order: index + 1
      }));

      try {
        await onReorder(reorderData);
        onRefresh();
      } catch (error) {        // Revert on error
        setLocalSections(sections);
      }
    }
  };

  const handleToggleVisibility = async (sectionId: number, currentVisibility: boolean) => {
    try {
      await onToggleVisibility(sectionId, !currentVisibility);
      onRefresh();
    } catch (error) {    }
  };

  const handleDelete = async (sectionId: number) => {
    if (!window.confirm('Are you sure you want to delete this section? This action cannot be undone.')) {
      return;
    }

    try {
      await onDelete(sectionId);
      onRefresh();
    } catch (error) {    }
  };

  const handleEdit = (section: PageSection) => {
    setEditingSection(section);
  };

  const handleCloseEditor = () => {
    setEditingSection(null);
  };

  const handleSaveEditor = () => {
    setEditingSection(null);
    onRefresh();
  };

  if (localSections.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg
          className="w-16 h-16 mx-auto mb-4 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="text-lg font-medium">No sections found</p>
        <p className="text-sm mt-1">Add sections to get started</p>
      </div>
    );
  }

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={localSections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {localSections.map((section) => (
              <SortableSection
                key={section.id}
                section={section}
                language={language}
                onEdit={() => handleEdit(section)}
                onToggleVisibility={() => handleToggleVisibility(section.id, section.is_visible)}
                onDelete={() => handleDelete(section.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Section Editor Modal */}
      {editingSection && (
        <SectionEditor
          section={editingSection}
          language={language}
          onClose={handleCloseEditor}
          onSave={handleSaveEditor}
        />
      )}
    </>
  );
};

export default SectionList;
